import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, getStatusInfo, formatDate, COUNTRIES } from "../lib/utils";
import { useAuth } from "../App";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Users,
  Plus,
  Search,
  ChevronRight,
  Filter,
  Briefcase,
  GraduationCap,
  History,
  Star,
  FileCheck,
  Trash2,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Building,
  Edit,
  MoreVertical,
  Mail,
  Send,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function Candidates() {
  const { user: currentUser } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(currentUser?.role);
  const [candidates, setCandidates] = useState([]);
  const [pendingCandidates, setPendingCandidates] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [ipcbs, setIpcbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedPendingCandidate, setSelectedPendingCandidate] = useState(null);
  const [approvalData, setApprovalData] = useState({ institution_id: "", ipcb_id: "", assigned_assessor: "", assigned_iqa: "" });
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("personal");
  
  // Edit and Delete states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [editActiveTab, setEditActiveTab] = useState("personal");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  
  // Assignment states
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningCandidate, setAssigningCandidate] = useState(null);
  const [staffUsers, setStaffUsers] = useState([]);
  const [assignData, setAssignData] = useState({ assigned_assessor: "", assigned_iqa: "" });

  // Invite states
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteData, setInviteData] = useState({ name: "", email: "", institution_id: "", ipcb_id: "" });
  const [sendingInvite, setSendingInvite] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Details
    full_name: "",
    email: "",
    passport_number: "",
    nationality: "",
    phone: "",
    // Current Employment (High Priority)
    current_employer: "",
    current_role: "",
    years_experience: "",
    key_responsibilities: "",
    // Target Industry
    target_industry: "",
    target_sub_industry: "",
    // Training & Certifications
    training_certifications: [],
    // Employment History
    employment_history: [],
    // Self-Assessment
    self_rating: "",
    self_rating_justification: "",
    // Declaration
    declaration_accepted: false,
    // Institution & IPCB
    institution_id: "",
    ipcb_id: "",
    // Assignment
    assigned_assessor: "",
    assigned_iqa: "",
  });

  // Temporary state for adding new training/employment entries
  const [newTraining, setNewTraining] = useState({ name: "", date: "", issuer: "" });
  const [newEmployment, setNewEmployment] = useState({ 
    employer: "", role: "", from_date: "", to_date: "", duties: "", employment_type: "full-time" 
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [candidatesRes, institutionsRes, ipcbsRes, pendingRes, usersRes] = await Promise.all([
        api.get("/candidates"),
        api.get("/institutions"),
        api.get("/ipcbs"),
        api.get("/candidates/pending-approval").catch(() => ({ data: [] })),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setCandidates(candidatesRes.data);
      setInstitutions(institutionsRes.data);
      setIpcbs(ipcbsRes.data);
      setPendingCandidates(pendingRes.data || []);
      const users = usersRes.data || [];
      setStaffUsers(users.filter(u => ["assessor", "iqa", "admin", "super_admin"].includes(u.role)));
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveCandidate = async () => {
    if (!approvalData.institution_id || !approvalData.ipcb_id) {
      toast.error("Please select both Institution and IPCB");
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/candidates/${selectedPendingCandidate.candidate_id}/approve`, approvalData);
      // Also assign staff if selected during approval
      if (approvalData.assigned_assessor || approvalData.assigned_iqa) {
        await api.put(`/candidates/${selectedPendingCandidate.candidate_id}/assign`, {
          assigned_assessor: approvalData.assigned_assessor || null,
          assigned_iqa: approvalData.assigned_iqa || null,
        });
      }
      toast.success("Candidate approved successfully! They will receive an email notification.");
      setApprovalDialogOpen(false);
      setSelectedPendingCandidate(null);
      setApprovalData({ institution_id: "", ipcb_id: "", assigned_assessor: "", assigned_iqa: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve candidate");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectCandidate = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/candidates/${selectedPendingCandidate.candidate_id}/reject-application`, {
        reason: rejectReason
      });
      toast.success("Application rejected");
      setRejectDialogOpen(false);
      setSelectedPendingCandidate(null);
      setRejectReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject application");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.declaration_accepted) {
      toast.error("Please accept the declaration to proceed");
      setActiveTab("declaration");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
        self_rating: formData.self_rating ? parseInt(formData.self_rating) : null,
        training_certifications: formData.training_certifications.length > 0 ? formData.training_certifications : null,
        employment_history: formData.employment_history.length > 0 ? formData.employment_history : null,
      };
      // Remove assignment fields from candidate payload (handled separately)
      const { assigned_assessor, assigned_iqa, ...candidatePayload } = payload;
      const res = await api.post("/candidates", candidatePayload);
      
      // Assign staff if selected
      if (assigned_assessor || assigned_iqa) {
        const candidateId = res.data?.candidate_id;
        if (candidateId) {
          await api.put(`/candidates/${candidateId}/assign`, {
            assigned_assessor: assigned_assessor || null,
            assigned_iqa: assigned_iqa || null,
          });
        }
      }
      
      toast.success("Candidate created successfully");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create candidate");
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-save when changing tabs
  const handleTabChange = (newTab) => {
    // Save current form data to localStorage for persistence
    localStorage.setItem('candidateFormDraft', JSON.stringify(formData));
    setActiveTab(newTab);
  };

  // Load draft from localStorage when dialog opens
  const handleDialogOpenChange = (open) => {
    if (open && !editingCandidate) {
      // Only load draft for NEW candidates, not when editing
      resetForm();
      localStorage.removeItem('candidateFormDraft');
    } else if (!open) {
      // Clear draft when dialog closes
      localStorage.removeItem('candidateFormDraft');
    }
    setDialogOpen(open);
  };

  // Edit candidate handler
  const handleEditCandidate = (candidate) => {
    setEditingCandidate(candidate);
    setFormData({
      full_name: candidate.full_name || "",
      email: candidate.email || "",
      passport_number: candidate.passport_number || "",
      nationality: candidate.nationality || "",
      phone: candidate.phone || "",
      current_employer: candidate.current_employer || "",
      current_role: candidate.current_role || "",
      years_experience: candidate.years_experience || "",
      key_responsibilities: candidate.key_responsibilities || "",
      target_industry: candidate.target_industry || "",
      target_sub_industry: candidate.target_sub_industry || "",
      training_certifications: candidate.training_certifications || [],
      employment_history: candidate.employment_history || [],
      self_rating: candidate.self_rating || "",
      self_rating_justification: candidate.self_rating_justification || "",
      declaration_accepted: candidate.declaration_accepted || false,
      institution_id: candidate.institution_id || "",
      ipcb_id: candidate.ipcb_id || "",
      assigned_assessor: candidate.assigned_assessor || "",
      assigned_iqa: candidate.assigned_iqa || "",
    });
    setEditDialogOpen(true);
  };

  const handleUpdateCandidate = async (e) => {
    e.preventDefault();
    if (!editingCandidate) return;
    
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
        self_rating: formData.self_rating ? parseInt(formData.self_rating) : null,
      };
      await api.put(`/candidates/${editingCandidate.candidate_id}`, payload);
      toast.success("Candidate updated successfully");
      setEditDialogOpen(false);
      setEditingCandidate(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update candidate");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete candidate handler
  const handleDeleteCandidate = async () => {
    if (!candidateToDelete) return;
    
    setSubmitting(true);
    try {
      await api.delete(`/candidates/${candidateToDelete.candidate_id}`);
      toast.success("Candidate deleted successfully");
      setDeleteDialogOpen(false);
      setCandidateToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete candidate");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenAssign = (candidate) => {
    setAssigningCandidate(candidate);
    setAssignData({
      assigned_assessor: candidate.assigned_assessor || "",
      assigned_iqa: candidate.assigned_iqa || "",
    });
    setAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    if (!assigningCandidate) return;
    setSubmitting(true);
    try {
      await api.put(`/candidates/${assigningCandidate.candidate_id}/assign`, assignData);
      toast.success("Staff assignment updated");
      setAssignDialogOpen(false);
      setAssigningCandidate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign staff");
    } finally {
      setSubmitting(false);
    }
  };

  const getStaffName = (userId) => {
    if (!userId) return null;
    const user = staffUsers.find(u => u.user_id === userId);
    return user ? user.name : userId;
  };

  const handleQuickInvite = async () => {
    if (!inviteData.name.trim() || !inviteData.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSendingInvite(true);
    try {
      const resp = await api.post("/candidates/quick-invite", inviteData);
      const isReinvite = resp?.data?.re_invited;
      toast.success(
        isReinvite
          ? `Re-invitation sent to ${inviteData.email}. Password reset to User@123.`
          : `Invitation sent to ${inviteData.email}`,
        { duration: 6000 }
      );
      setInviteDialogOpen(false);
      setInviteData({ name: "", email: "", institution_id: "", ipcb_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSendingInvite(false);
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: "",
      email: "",
      passport_number: "",
      nationality: "",
      phone: "",
      current_employer: "",
      current_role: "",
      years_experience: "",
      key_responsibilities: "",
      target_industry: "",
      target_sub_industry: "",
      training_certifications: [],
      employment_history: [],
      self_rating: "",
      self_rating_justification: "",
      declaration_accepted: false,
      institution_id: "",
      ipcb_id: "",
      assigned_assessor: "",
      assigned_iqa: "",
    });
    setActiveTab("personal");
    setNewTraining({ name: "", date: "", issuer: "" });
    setNewEmployment({ employer: "", role: "", from_date: "", to_date: "", duties: "", employment_type: "full-time" });
  };

  const addTraining = () => {
    if (newTraining.name && newTraining.date) {
      setFormData({
        ...formData,
        training_certifications: [...formData.training_certifications, { ...newTraining }]
      });
      setNewTraining({ name: "", date: "", issuer: "" });
    }
  };

  const removeTraining = (index) => {
    setFormData({
      ...formData,
      training_certifications: formData.training_certifications.filter((_, i) => i !== index)
    });
  };

  const addEmployment = () => {
    if (newEmployment.employer && newEmployment.role) {
      setFormData({
        ...formData,
        employment_history: [...formData.employment_history, { ...newEmployment }]
      });
      setNewEmployment({ employer: "", role: "", from_date: "", to_date: "", duties: "", employment_type: "full-time" });
    }
  };

  const removeEmployment = (index) => {
    setFormData({
      ...formData,
      employment_history: formData.employment_history.filter((_, i) => i !== index)
    });
  };

  const filteredCandidates = candidates.filter((c) => {
    const matchesSearch =
      c.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase());
    let matchesStatus = statusFilter === "all";
    if (!matchesStatus) {
      if (statusFilter === "approved") {
        // "Approved" includes report_generated and closed — all post-approval stages
        matchesStatus = ["approved", "report_generated", "closed"].includes(c.case_status);
      } else if (statusFilter === "interview") {
        matchesStatus = ["interview_scheduled", "interview_pending", "interview_failed"].includes(c.case_status);
      } else {
        matchesStatus = c.case_status === statusFilter;
      }
    }
    return matchesSearch && matchesStatus;
  });

  const selfRatingLabels = {
    1: "Beginner - Limited experience",
    2: "Basic - Some experience, need guidance",
    3: "Intermediate - Can work independently",
    4: "Advanced - Highly skilled, can mentor",
    5: "Expert - Industry leader level"
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="candidates-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Candidates</h1>
            <p className="text-slate-500 mt-1">Manage RPL candidate assessments</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setInviteDialogOpen(true)} data-testid="invite-candidate-btn">
              <Mail className="w-4 h-4 mr-2" />
              Invite Candidate
            </Button>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-candidate-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Candidate
            </Button>
          </div>
        </div>

        {/* Add/Edit Candidate Dialog */}
        <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Candidate</DialogTitle>
                <DialogDescription>
                  Complete the candidate information form. Fields marked with * are required.
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="mt-4">
                <Tabs value={activeTab} onValueChange={handleTabChange}>
                  <TabsList className="grid grid-cols-5 mb-4">
                    <TabsTrigger value="personal" className="text-xs">
                      <Users className="w-3 h-3 mr-1" />
                      Personal
                    </TabsTrigger>
                    <TabsTrigger value="employment" className="text-xs">
                      <Briefcase className="w-3 h-3 mr-1" />
                      Employment
                    </TabsTrigger>
                    <TabsTrigger value="training" className="text-xs">
                      <GraduationCap className="w-3 h-3 mr-1" />
                      Training
                    </TabsTrigger>
                    <TabsTrigger value="history" className="text-xs">
                      <History className="w-3 h-3 mr-1" />
                      History
                    </TabsTrigger>
                    <TabsTrigger value="assignment" className="text-xs">
                      <Users className="w-3 h-3 mr-1" />
                      Assignment
                    </TabsTrigger>
                    <TabsTrigger value="declaration" className="text-xs">
                      <FileCheck className="w-3 h-3 mr-1" />
                      Declaration
                    </TabsTrigger>
                  </TabsList>

                  {/* Tab 1: Personal Details */}
                  <TabsContent value="personal" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="full_name">Full Name *</Label>
                        <Input
                          id="full_name"
                          data-testid="candidate-name-input"
                          value={formData.full_name}
                          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                          placeholder="Enter full name as per IC/Passport"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="candidate-email-input"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="email@example.com"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="passport_number">Passport/IC Number</Label>
                        <Input
                          id="passport_number"
                          data-testid="candidate-passport-input"
                          value={formData.passport_number}
                          onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })}
                          placeholder="e.g., A12345678"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="nationality">Nationality / Country</Label>
                        <Select
                          value={formData.nationality}
                          onValueChange={(value) => setFormData({ ...formData, nationality: value })}
                        >
                          <SelectTrigger data-testid="candidate-nationality-select">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {COUNTRIES.map((country) => (
                              <SelectItem key={country} value={country}>
                                {country}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          data-testid="candidate-phone-input"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+60 12-345 6789"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="target_industry">Target Industry</Label>
                        <Input
                          id="target_industry"
                          value={formData.target_industry}
                          onChange={(e) => setFormData({ ...formData, target_industry: e.target.value })}
                          placeholder="e.g., Information Technology"
                          data-testid="candidate-industry-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="institution_id">Awarding Institution *</Label>
                        <Select
                          value={formData.institution_id}
                          onValueChange={(value) => setFormData({ ...formData, institution_id: value })}
                        >
                          <SelectTrigger data-testid="candidate-institution-select">
                            <SelectValue placeholder="Select institution" />
                          </SelectTrigger>
                          <SelectContent>
                            {institutions.map((inst) => (
                              <SelectItem key={inst.institution_id} value={inst.institution_id}>
                                {inst.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ipcb_id">IPCB Board *</Label>
                        <Select
                          value={formData.ipcb_id}
                          onValueChange={(value) => setFormData({ ...formData, ipcb_id: value })}
                        >
                          <SelectTrigger data-testid="candidate-ipcb-select">
                            <SelectValue placeholder="Select IPCB" />
                          </SelectTrigger>
                          <SelectContent>
                            {ipcbs.map((ipcb) => (
                              <SelectItem key={ipcb.ipcb_id} value={ipcb.ipcb_id}>
                                {ipcb.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Tab 2: Current Employment */}
                  <TabsContent value="employment" className="space-y-4">
                    <Card className="border-blue-200 bg-blue-50">
                      <CardContent className="p-3">
                        <p className="text-sm text-blue-700">
                          <strong>Important:</strong> This information helps the AI engine better understand your experience and recommend appropriate qualifications.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="current_employer">Current Employer</Label>
                        <Input
                          id="current_employer"
                          data-testid="candidate-employer-input"
                          value={formData.current_employer}
                          onChange={(e) => setFormData({ ...formData, current_employer: e.target.value })}
                          placeholder="Company name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="current_role">Current Job Title</Label>
                        <Input
                          id="current_role"
                          data-testid="candidate-role-input"
                          value={formData.current_role}
                          onChange={(e) => setFormData({ ...formData, current_role: e.target.value })}
                          placeholder="e.g., Senior Project Manager"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="years_experience">Years in Current Role</Label>
                        <Input
                          id="years_experience"
                          type="number"
                          data-testid="candidate-experience-input"
                          value={formData.years_experience}
                          onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })}
                          placeholder="e.g., 5"
                          min="0"
                          max="50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Self-Assessment Rating</Label>
                        <Select
                          value={formData.self_rating}
                          onValueChange={(value) => setFormData({ ...formData, self_rating: value })}
                        >
                          <SelectTrigger data-testid="candidate-rating-select">
                            <SelectValue placeholder="Rate your experience level" />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5].map((rating) => (
                              <SelectItem key={rating} value={String(rating)}>
                                <div className="flex items-center gap-2">
                                  <div className="flex">
                                    {[...Array(rating)].map((_, i) => (
                                      <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                                    ))}
                                  </div>
                                  <span className="text-xs text-slate-500">{selfRatingLabels[rating]}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="key_responsibilities">Key Tasks & Responsibilities</Label>
                      <Textarea
                        id="key_responsibilities"
                        data-testid="candidate-responsibilities-input"
                        value={formData.key_responsibilities}
                        onChange={(e) => setFormData({ ...formData, key_responsibilities: e.target.value })}
                        placeholder="List the main tasks you perform that are relevant to your RPL application. Be specific about your responsibilities, projects managed, and skills used..."
                        rows={5}
                      />
                      <p className="text-xs text-slate-500">This helps the AI match your experience to qualification competencies</p>
                    </div>
                  </TabsContent>

                  {/* Tab 3: Training & Certifications */}
                  <TabsContent value="training" className="space-y-4">
                    <Card className="border-amber-200 bg-amber-50">
                      <CardContent className="p-3">
                        <p className="text-sm text-amber-700">
                          Add any training courses, certifications, or professional development you've completed.
                        </p>
                      </CardContent>
                    </Card>

                    {/* Add Training Form */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Course/Certification Name</Label>
                        <Input
                          value={newTraining.name}
                          onChange={(e) => setNewTraining({ ...newTraining, name: e.target.value })}
                          placeholder="e.g., PMP Certification"
                          data-testid="training-name-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Completion Date</Label>
                        <Input
                          type="month"
                          value={newTraining.date}
                          onChange={(e) => setNewTraining({ ...newTraining, date: e.target.value })}
                          data-testid="training-date-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Issuing Organization</Label>
                        <div className="flex gap-2">
                          <Input
                            value={newTraining.issuer}
                            onChange={(e) => setNewTraining({ ...newTraining, issuer: e.target.value })}
                            placeholder="e.g., PMI"
                            data-testid="training-issuer-input"
                          />
                          <Button 
                            type="button" 
                            size="sm" 
                            onClick={addTraining}
                            disabled={!newTraining.name || !newTraining.date}
                            data-testid="add-training-btn"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Training List */}
                    {formData.training_certifications.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Course/Certification</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Issuer</TableHead>
                              <TableHead className="w-10"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {formData.training_certifications.map((training, index) => (
                              <TableRow key={index}>
                                <TableCell className="font-medium">{training.name}</TableCell>
                                <TableCell>{training.date}</TableCell>
                                <TableCell>{training.issuer || "-"}</TableCell>
                                <TableCell>
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => removeTraining(index)}
                                    className="text-red-500 hover:text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {formData.training_certifications.length === 0 && (
                      <div className="text-center py-8 border rounded-lg border-dashed">
                        <GraduationCap className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No training added yet</p>
                        <p className="text-xs text-slate-400">Add your relevant courses and certifications above</p>
                      </div>
                    )}
                  </TabsContent>

                  {/* Tab 4: Employment History */}
                  <TabsContent value="history" className="space-y-4">
                    <Card className="border-purple-200 bg-purple-50">
                      <CardContent className="p-3">
                        <p className="text-sm text-purple-700">
                          Add your previous employment history (last 3-5 positions recommended).
                        </p>
                      </CardContent>
                    </Card>

                    {/* Add Employment Form */}
                    <div className="space-y-3 p-4 border rounded-lg bg-slate-50">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Employer Name</Label>
                          <Input
                            value={newEmployment.employer}
                            onChange={(e) => setNewEmployment({ ...newEmployment, employer: e.target.value })}
                            placeholder="Company name"
                            data-testid="employment-employer-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Job Title</Label>
                          <Input
                            value={newEmployment.role}
                            onChange={(e) => setNewEmployment({ ...newEmployment, role: e.target.value })}
                            placeholder="Position held"
                            data-testid="employment-role-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">From Date</Label>
                          <Input
                            type="month"
                            value={newEmployment.from_date}
                            onChange={(e) => setNewEmployment({ ...newEmployment, from_date: e.target.value })}
                            data-testid="employment-from-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">To Date</Label>
                          <Input
                            type="month"
                            value={newEmployment.to_date}
                            onChange={(e) => setNewEmployment({ ...newEmployment, to_date: e.target.value })}
                            placeholder="Leave blank if current"
                            data-testid="employment-to-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Employment Type</Label>
                          <Select
                            value={newEmployment.employment_type}
                            onValueChange={(value) => setNewEmployment({ ...newEmployment, employment_type: value })}
                          >
                            <SelectTrigger data-testid="employment-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="full-time">Full-time</SelectItem>
                              <SelectItem value="part-time">Part-time</SelectItem>
                              <SelectItem value="contract">Contract</SelectItem>
                              <SelectItem value="freelance">Freelance</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Major Duties</Label>
                        <div className="flex gap-2">
                          <Textarea
                            value={newEmployment.duties}
                            onChange={(e) => setNewEmployment({ ...newEmployment, duties: e.target.value })}
                            placeholder="Brief description of key responsibilities..."
                            rows={2}
                            className="flex-1"
                            data-testid="employment-duties-input"
                          />
                          <Button 
                            type="button" 
                            onClick={addEmployment}
                            disabled={!newEmployment.employer || !newEmployment.role}
                            data-testid="add-employment-btn"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Employment List */}
                    {formData.employment_history.length > 0 && (
                      <div className="space-y-3">
                        {formData.employment_history.map((emp, index) => (
                          <Card key={index} className="border-slate-200">
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-medium text-slate-900">{emp.role}</h4>
                                  <p className="text-sm text-slate-600">{emp.employer}</p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {emp.from_date} - {emp.to_date || "Present"} • {emp.employment_type}
                                  </p>
                                  {emp.duties && (
                                    <p className="text-sm text-slate-500 mt-2">{emp.duties}</p>
                                  )}
                                </div>
                                <Button 
                                  type="button" 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => removeEmployment(index)}
                                  className="text-red-500 hover:text-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}

                    {formData.employment_history.length === 0 && (
                      <div className="text-center py-8 border rounded-lg border-dashed">
                        <History className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No employment history added yet</p>
                        <p className="text-xs text-slate-400">You may also attach a current résumé instead</p>
                      </div>
                    )}
                  </TabsContent>

                  {/* Tab: Assignment */}
                  <TabsContent value="assignment" className="space-y-4">
                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-600" />
                          Task Assignment
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>Assign Assessor</Label>
                          <Select
                            value={formData.assigned_assessor || "none"}
                            onValueChange={(v) => setFormData({ ...formData, assigned_assessor: v === "none" ? "" : v })}
                          >
                            <SelectTrigger data-testid="form-assessor-select">
                              <SelectValue placeholder="Select assessor..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Assign Later</SelectItem>
                              {staffUsers.filter(u => ["assessor", "admin", "super_admin"].includes(u.role)).map(u => (
                                <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-slate-500">The assessor will score the candidate's evidence against competencies.</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Assign IQA (Internal Quality Assurer)</Label>
                          <Select
                            value={formData.assigned_iqa || "none"}
                            onValueChange={(v) => setFormData({ ...formData, assigned_iqa: v === "none" ? "" : v })}
                          >
                            <SelectTrigger data-testid="form-iqa-select">
                              <SelectValue placeholder="Select IQA..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Assign Later</SelectItem>
                              {staffUsers.filter(u => ["iqa", "admin", "super_admin"].includes(u.role)).map(u => (
                                <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-slate-500">The IQA will verify the assessment and schedule interviews.</p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                            <p className="text-xs text-blue-700">
                              You can also assign or change staff later from the candidates list using the "Assign Staff" option.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Tab 5: Declaration */}
                  <TabsContent value="declaration" className="space-y-4">
                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <FileCheck className="w-5 h-5 text-emerald-600" />
                          Declaration
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-700 space-y-3">
                          <p>By submitting this application, I declare that:</p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>All information provided in this application is true and correct to the best of my knowledge.</li>
                            <li>All documents submitted are genuine and have not been tampered with.</li>
                            <li>I understand that providing false information may result in the rejection of my application or revocation of any award granted.</li>
                            <li>I consent to the processing of my personal data for the purposes of this RPL assessment.</li>
                            <li>I agree to provide additional evidence or attend an interview if required.</li>
                          </ul>
                        </div>

                        <div className="flex items-start space-x-3 p-4 border rounded-lg bg-amber-50 border-amber-200">
                          <Checkbox
                            id="declaration"
                            checked={formData.declaration_accepted}
                            onCheckedChange={(checked) => setFormData({ ...formData, declaration_accepted: checked })}
                            data-testid="declaration-checkbox"
                          />
                          <div className="space-y-1">
                            <Label htmlFor="declaration" className="font-medium text-amber-800 cursor-pointer">
                              I accept the above declaration *
                            </Label>
                            <p className="text-xs text-amber-600">
                              You must accept this declaration to proceed with your application.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>

                <div className="flex justify-between items-center pt-4 border-t mt-4">
                  <div className="text-sm text-slate-500">
                    {activeTab !== "personal" && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          const tabs = ["personal", "employment", "training", "history", "assignment", "declaration"];
                          const currentIndex = tabs.indexOf(activeTab);
                          if (currentIndex > 0) setActiveTab(tabs[currentIndex - 1]);
                        }}
                      >
                        Previous
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    {activeTab !== "declaration" ? (
                      <Button 
                        type="button"
                        onClick={() => {
                          const tabs = ["personal", "employment", "training", "history", "assignment", "declaration"];
                          const currentIndex = tabs.indexOf(activeTab);
                          if (currentIndex < tabs.length - 1) setActiveTab(tabs[currentIndex + 1]);
                        }}
                      >
                        Next
                      </Button>
                    ) : (
                      <Button 
                        type="submit" 
                        disabled={submitting || !formData.declaration_accepted}
                        data-testid="create-candidate-submit-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          "Create Candidate"
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search candidates..."
              className="pl-10"
              data-testid="search-candidates-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending_approval">Pending Approval</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="documents_uploaded">Documents Uploaded</SelectItem>
              <SelectItem value="ai_review">Credentia Review</SelectItem>
              <SelectItem value="assessor_review">Assessor Review</SelectItem>
              <SelectItem value="interview">Interview</SelectItem>
              <SelectItem value="iqa_review">IQA Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Pending Approvals Section */}
        {pendingCandidates.length > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <Clock className="w-5 h-5" />
                Pending Approvals ({pendingCandidates.length})
              </CardTitle>
              <p className="text-sm text-amber-700">
                These candidates have self-registered and need your review before they can upload documents.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingCandidates.map((candidate) => (
                  <div 
                    key={candidate.candidate_id}
                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-amber-200"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{candidate.full_name}</p>
                          <p className="text-sm text-slate-500">{candidate.email}</p>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
                        {candidate.current_role && (
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-3 h-3" />
                            {candidate.current_role}
                          </span>
                        )}
                        {candidate.current_employer && (
                          <span className="flex items-center gap-1">
                            <Building className="w-3 h-3" />
                            {candidate.current_employer}
                          </span>
                        )}
                        {candidate.nationality && (
                          <span>{candidate.nationality}</span>
                        )}
                        <span>Applied: {formatDate(candidate.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => {
                          setSelectedPendingCandidate(candidate);
                          setRejectDialogOpen(true);
                        }}
                        data-testid={`reject-${candidate.candidate_id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          setSelectedPendingCandidate(candidate);
                          setApprovalDialogOpen(true);
                        }}
                        data-testid={`approve-${candidate.candidate_id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Approval Dialog */}
        <Dialog open={approvalDialogOpen} onOpenChange={(open) => {
          setApprovalDialogOpen(open);
          if (!open) {
            setSelectedPendingCandidate(null);
            setApprovalData({ institution_id: "", ipcb_id: "", assigned_assessor: "", assigned_iqa: "" });
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Approve Application
              </DialogTitle>
              <DialogDescription>
                Assign the candidate to an institution and IPCB board to approve their application.
              </DialogDescription>
            </DialogHeader>
            
            {selectedPendingCandidate && (
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedPendingCandidate.full_name}</p>
                  <p className="text-sm text-slate-500">{selectedPendingCandidate.email}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Awarding Institution *</Label>
                  <Select
                    value={approvalData.institution_id}
                    onValueChange={(value) => setApprovalData({ ...approvalData, institution_id: value })}
                  >
                    <SelectTrigger data-testid="approval-institution-select">
                      <SelectValue placeholder="Select institution" />
                    </SelectTrigger>
                    <SelectContent>
                      {institutions.map((inst) => (
                        <SelectItem key={inst.institution_id} value={inst.institution_id}>
                          {inst.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>IPCB Board *</Label>
                  <Select
                    value={approvalData.ipcb_id}
                    onValueChange={(value) => setApprovalData({ ...approvalData, ipcb_id: value })}
                  >
                    <SelectTrigger data-testid="approval-ipcb-select">
                      <SelectValue placeholder="Select IPCB" />
                    </SelectTrigger>
                    <SelectContent>
                      {ipcbs.map((ipcb) => (
                        <SelectItem key={ipcb.ipcb_id} value={ipcb.ipcb_id}>
                          {ipcb.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Assign Assessor</Label>
                  <Select
                    value={approvalData.assigned_assessor || "none"}
                    onValueChange={(value) => setApprovalData({ ...approvalData, assigned_assessor: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="approval-assessor-select">
                      <SelectValue placeholder="Select assessor (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Assign Later</SelectItem>
                      {staffUsers.filter(u => ["assessor", "admin", "super_admin"].includes(u.role)).map(u => (
                        <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Assign IQA</Label>
                  <Select
                    value={approvalData.assigned_iqa || "none"}
                    onValueChange={(value) => setApprovalData({ ...approvalData, assigned_iqa: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="approval-iqa-select">
                      <SelectValue placeholder="Select IQA (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Assign Later</SelectItem>
                      {staffUsers.filter(u => ["iqa", "admin", "super_admin"].includes(u.role)).map(u => (
                        <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                    <p className="text-xs text-blue-700">
                      Once approved, the candidate will receive an email notification and will be able to upload their documents.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-3 justify-end pt-2">
                  <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={handleApproveCandidate}
                    disabled={submitting || !approvalData.institution_id || !approvalData.ipcb_id}
                    data-testid="confirm-approve-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Approving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve & Send Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={(open) => {
          setRejectDialogOpen(open);
          if (!open) {
            setSelectedPendingCandidate(null);
            setRejectReason("");
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="w-5 h-5" />
                Reject Application
              </DialogTitle>
              <DialogDescription>
                Please provide a reason for rejecting this application.
              </DialogDescription>
            </DialogHeader>
            
            {selectedPendingCandidate && (
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedPendingCandidate.full_name}</p>
                  <p className="text-sm text-slate-500">{selectedPendingCandidate.email}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Rejection Reason *</Label>
                  <Textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Please explain why this application is being rejected..."
                    rows={4}
                    data-testid="reject-reason-input"
                  />
                </div>
                
                <div className="flex gap-3 justify-end pt-2">
                  <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={handleRejectCandidate}
                    disabled={submitting || !rejectReason.trim()}
                    data-testid="confirm-reject-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Rejecting...
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject Application
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : filteredCandidates.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Role / Employer</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCandidates.map((candidate) => {
                    const statusInfo = getStatusInfo(candidate.case_status);
                    return (
                      <TableRow
                        key={candidate.candidate_id}
                        className="hover:bg-slate-50"
                        data-testid={`candidate-row-${candidate.candidate_id}`}
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{candidate.full_name}</p>
                            <p className="text-sm text-slate-500">{candidate.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-slate-900">{candidate.current_role || "-"}</p>
                            <p className="text-sm text-slate-500">{candidate.current_employer || "-"}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {candidate.assigned_assessor ? (
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium text-slate-500 uppercase w-14">Assessor</span>
                                <Badge variant="outline" className="text-xs">{getStaffName(candidate.assigned_assessor)}</Badge>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium text-slate-500 uppercase w-14">Assessor</span>
                                <span className="text-xs text-slate-400">Unassigned</span>
                              </div>
                            )}
                            {candidate.assigned_iqa ? (
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium text-purple-500 uppercase w-14">IQA</span>
                                <Badge variant="outline" className="text-xs border-purple-200 text-purple-700">{getStaffName(candidate.assigned_iqa)}</Badge>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium text-purple-500 uppercase w-14">IQA</span>
                                <span className="text-xs text-slate-400">Unassigned</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusInfo.color}>
                            {statusInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {formatDate(candidate.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Link to={`/candidates/${candidate.candidate_id}`}>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                data-testid={`view-candidate-${candidate.candidate_id}`}
                              >
                                <ChevronRight className="w-5 h-5" />
                              </Button>
                            </Link>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleOpenAssign(candidate)}>
                                  <Users className="w-4 h-4 mr-2" />
                                  Assign Staff
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEditCandidate(candidate)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Details
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setCandidateToDelete(candidate);
                                    setDeleteDialogOpen(true);
                                  }}
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No candidates found</h3>
                <p className="text-slate-500 mb-4">
                  {searchTerm || statusFilter !== "all"
                    ? "Try adjusting your search or filter"
                    : "Get started by adding your first candidate"}
                </p>
                {!searchTerm && statusFilter === "all" && (
                  <Button onClick={() => setDialogOpen(true)} data-testid="empty-state-add-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Candidate
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Candidate Dialog - Full 5-Tab Version */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) {
            setEditingCandidate(null);
            setEditActiveTab("personal");
            resetForm();
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Candidate: {editingCandidate?.full_name}</DialogTitle>
              <DialogDescription>
                Update candidate information across all categories
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateCandidate}>
              <Tabs value={editActiveTab} onValueChange={setEditActiveTab} className="mt-4">
                <TabsList className="grid grid-cols-5 mb-4">
                  <TabsTrigger value="personal" className="text-xs">
                    <Users className="w-3 h-3 mr-1" />
                    Personal
                  </TabsTrigger>
                  <TabsTrigger value="employment" className="text-xs">
                    <Briefcase className="w-3 h-3 mr-1" />
                    Employment
                  </TabsTrigger>
                  <TabsTrigger value="training" className="text-xs">
                    <GraduationCap className="w-3 h-3 mr-1" />
                    Training
                  </TabsTrigger>
                  <TabsTrigger value="history" className="text-xs">
                    <History className="w-3 h-3 mr-1" />
                    History
                  </TabsTrigger>
                  <TabsTrigger value="declaration" className="text-xs">
                    <FileCheck className="w-3 h-3 mr-1" />
                    Declaration
                  </TabsTrigger>
                </TabsList>

                {/* Tab 1: Personal Details */}
                <TabsContent value="personal" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        value={formData.full_name}
                        onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                        placeholder="Enter full name as per IC/Passport"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="email@example.com"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Passport/IC Number</Label>
                      <Input
                        value={formData.passport_number}
                        onChange={(e) => setFormData({...formData, passport_number: e.target.value})}
                        placeholder="e.g., A12345678"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nationality / Country</Label>
                      <Select
                        value={formData.nationality}
                        onValueChange={(v) => setFormData({...formData, nationality: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phone Number</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="+60 12-345 6789"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Institution *</Label>
                      <Select
                        value={formData.institution_id}
                        onValueChange={(v) => setFormData({...formData, institution_id: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select institution" />
                        </SelectTrigger>
                        <SelectContent>
                          {institutions.map((inst) => (
                            <SelectItem key={inst.institution_id} value={inst.institution_id}>
                              {inst.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>IPCB Board *</Label>
                    <Select
                      value={formData.ipcb_id}
                      onValueChange={(v) => setFormData({...formData, ipcb_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select IPCB" />
                      </SelectTrigger>
                      <SelectContent>
                        {ipcbs.map((ipcb) => (
                          <SelectItem key={ipcb.ipcb_id} value={ipcb.ipcb_id}>
                            {ipcb.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>

                {/* Tab 2: Employment */}
                <TabsContent value="employment" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Current Employer</Label>
                      <Input
                        value={formData.current_employer}
                        onChange={(e) => setFormData({...formData, current_employer: e.target.value})}
                        placeholder="Company name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Current Role / Job Title</Label>
                      <Input
                        value={formData.current_role}
                        onChange={(e) => setFormData({...formData, current_role: e.target.value})}
                        placeholder="e.g., Senior Manager"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Years of Experience</Label>
                    <Input
                      type="number"
                      value={formData.years_experience}
                      onChange={(e) => setFormData({...formData, years_experience: e.target.value})}
                      placeholder="Total years in this field"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Key Responsibilities</Label>
                    <Textarea
                      value={formData.key_responsibilities}
                      onChange={(e) => setFormData({...formData, key_responsibilities: e.target.value})}
                      placeholder="Describe your main duties and responsibilities..."
                      rows={4}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Target Industry</Label>
                      <Input
                        value={formData.target_industry}
                        onChange={(e) => setFormData({...formData, target_industry: e.target.value})}
                        placeholder="e.g., Information Technology"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Target Sub-Industry</Label>
                      <Input
                        value={formData.target_sub_industry}
                        onChange={(e) => setFormData({...formData, target_sub_industry: e.target.value})}
                        placeholder="e.g., Software Development"
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 3: Training & Certifications */}
                <TabsContent value="training" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Training & Certifications</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({
                        ...formData,
                        training_certifications: [
                          ...formData.training_certifications,
                          { name: "", provider: "", year: "", certificate_number: "" }
                        ]
                      })}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Training
                    </Button>
                  </div>
                  {formData.training_certifications.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      No training/certifications added. Click "Add Training" to add one.
                    </p>
                  ) : (
                    formData.training_certifications.map((cert, index) => (
                      <div key={index} className="p-3 border rounded-lg space-y-3 bg-slate-50">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Training #{index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newCerts = [...formData.training_certifications];
                              newCerts.splice(index, 1);
                              setFormData({...formData, training_certifications: newCerts});
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            placeholder="Course/Certification Name"
                            value={cert.name}
                            onChange={(e) => {
                              const newCerts = [...formData.training_certifications];
                              newCerts[index].name = e.target.value;
                              setFormData({...formData, training_certifications: newCerts});
                            }}
                          />
                          <Input
                            placeholder="Provider/Institution"
                            value={cert.provider}
                            onChange={(e) => {
                              const newCerts = [...formData.training_certifications];
                              newCerts[index].provider = e.target.value;
                              setFormData({...formData, training_certifications: newCerts});
                            }}
                          />
                          <Input
                            placeholder="Year Completed"
                            value={cert.year}
                            onChange={(e) => {
                              const newCerts = [...formData.training_certifications];
                              newCerts[index].year = e.target.value;
                              setFormData({...formData, training_certifications: newCerts});
                            }}
                          />
                          <Input
                            placeholder="Certificate Number (optional)"
                            value={cert.certificate_number}
                            onChange={(e) => {
                              const newCerts = [...formData.training_certifications];
                              newCerts[index].certificate_number = e.target.value;
                              setFormData({...formData, training_certifications: newCerts});
                            }}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </TabsContent>

                {/* Tab 4: Employment History */}
                <TabsContent value="history" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Employment History</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({
                        ...formData,
                        employment_history: [
                          ...formData.employment_history,
                          { employer: "", role: "", start_year: "", end_year: "", responsibilities: "" }
                        ]
                      })}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Employment
                    </Button>
                  </div>
                  {formData.employment_history.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      No employment history added. Click "Add Employment" to add one.
                    </p>
                  ) : (
                    formData.employment_history.map((job, index) => (
                      <div key={index} className="p-3 border rounded-lg space-y-3 bg-slate-50">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Employment #{index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newHistory = [...formData.employment_history];
                              newHistory.splice(index, 1);
                              setFormData({...formData, employment_history: newHistory});
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            placeholder="Employer Name"
                            value={job.employer}
                            onChange={(e) => {
                              const newHistory = [...formData.employment_history];
                              newHistory[index].employer = e.target.value;
                              setFormData({...formData, employment_history: newHistory});
                            }}
                          />
                          <Input
                            placeholder="Job Title/Role"
                            value={job.role}
                            onChange={(e) => {
                              const newHistory = [...formData.employment_history];
                              newHistory[index].role = e.target.value;
                              setFormData({...formData, employment_history: newHistory});
                            }}
                          />
                          <Input
                            placeholder="Start Year"
                            value={job.start_year}
                            onChange={(e) => {
                              const newHistory = [...formData.employment_history];
                              newHistory[index].start_year = e.target.value;
                              setFormData({...formData, employment_history: newHistory});
                            }}
                          />
                          <Input
                            placeholder="End Year (or 'Present')"
                            value={job.end_year}
                            onChange={(e) => {
                              const newHistory = [...formData.employment_history];
                              newHistory[index].end_year = e.target.value;
                              setFormData({...formData, employment_history: newHistory});
                            }}
                          />
                        </div>
                        <Textarea
                          placeholder="Key responsibilities in this role..."
                          value={job.responsibilities}
                          onChange={(e) => {
                            const newHistory = [...formData.employment_history];
                            newHistory[index].responsibilities = e.target.value;
                            setFormData({...formData, employment_history: newHistory});
                          }}
                          rows={2}
                        />
                      </div>
                    ))
                  )}
                </TabsContent>

                {/* Tab 5: Declaration & Self Assessment */}
                <TabsContent value="declaration" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Self-Assessment Rating (1-5)</Label>
                    <Select
                      value={formData.self_rating?.toString()}
                      onValueChange={(v) => setFormData({...formData, self_rating: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Rate your competency level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 - Novice</SelectItem>
                        <SelectItem value="2">2 - Advanced Beginner</SelectItem>
                        <SelectItem value="3">3 - Competent</SelectItem>
                        <SelectItem value="4">4 - Proficient</SelectItem>
                        <SelectItem value="5">5 - Expert</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Justification for Self-Rating</Label>
                    <Textarea
                      value={formData.self_rating_justification}
                      onChange={(e) => setFormData({...formData, self_rating_justification: e.target.value})}
                      placeholder="Explain why you rated yourself at this level..."
                      rows={3}
                    />
                  </div>
                  <div className="flex items-start space-x-2 p-4 bg-blue-50 rounded-lg">
                    <Checkbox
                      id="edit-declaration"
                      checked={formData.declaration_accepted}
                      onCheckedChange={(checked) => setFormData({...formData, declaration_accepted: checked})}
                    />
                    <Label htmlFor="edit-declaration" className="text-sm leading-relaxed cursor-pointer">
                      I declare that all information provided is true and accurate to the best of my knowledge. 
                      I understand that providing false information may result in disqualification from the RPL process.
                    </Label>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end gap-3 pt-4 mt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Candidate</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{candidateToDelete?.full_name}</strong>? 
                This will permanently remove all their data including documents, assessments, and reports.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteCandidate}
                className="bg-red-600 hover:bg-red-700"
                disabled={submitting}
              >
                {submitting ? "Deleting..." : "Delete Candidate"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Assign Staff Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="max-w-md" data-testid="assign-staff-dialog">
            <DialogHeader>
              <DialogTitle>Assign Staff</DialogTitle>
              <DialogDescription>
                {isAdmin
                  ? <>Assign an Assessor and IQA to <strong>{assigningCandidate?.full_name}</strong></>
                  : <>Assign an IQA (Internal Quality Assurer) to <strong>{assigningCandidate?.full_name}</strong>. Only an admin can change the assigned assessor.</>
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Assessor {!isAdmin && <span className="text-xs text-slate-400">(admin-only)</span>}</Label>
                <Select
                  value={assignData.assigned_assessor || "none"}
                  onValueChange={(v) => setAssignData({ ...assignData, assigned_assessor: v === "none" ? "" : v })}
                  disabled={!isAdmin}
                >
                  <SelectTrigger data-testid="assign-assessor-select">
                    <SelectValue placeholder="Select assessor..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {staffUsers.filter(u => ["assessor", "admin", "super_admin"].includes(u.role)).map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>IQA (Internal Quality Assurer)</Label>
                <Select
                  value={assignData.assigned_iqa || "none"}
                  onValueChange={(v) => setAssignData({ ...assignData, assigned_iqa: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="assign-iqa-select">
                    <SelectValue placeholder="Select IQA..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {staffUsers.filter(u => ["iqa", "admin", "super_admin"].includes(u.role)).map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleAssign} disabled={submitting} data-testid="save-assignment-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Assignment
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invite Candidate Dialog */}
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogContent className="max-w-md" data-testid="invite-candidate-dialog">
            <DialogHeader>
              <DialogTitle>Invite Candidate</DialogTitle>
              <DialogDescription>
                Send a registration invitation. The candidate will receive an email with a link to fill in their details.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Candidate Name *</Label>
                <Input
                  value={inviteData.name}
                  onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                  placeholder="e.g., John Smith"
                  required
                  data-testid="invite-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  placeholder="e.g., john@email.com"
                  required
                  data-testid="invite-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Institution (optional)</Label>
                <Select
                  value={inviteData.institution_id || "none"}
                  onValueChange={(v) => setInviteData({ ...inviteData, institution_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select institution..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Assign Later</SelectItem>
                    {institutions.map(inst => (
                      <SelectItem key={inst.institution_id} value={inst.institution_id}>{inst.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>IPCB Board (optional)</Label>
                <Select
                  value={inviteData.ipcb_id || "none"}
                  onValueChange={(v) => setInviteData({ ...inviteData, ipcb_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select IPCB..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Assign Later</SelectItem>
                    {ipcbs.map(ipcb => (
                      <SelectItem key={ipcb.ipcb_id} value={ipcb.ipcb_id}>{ipcb.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Mail className="w-4 h-4 text-blue-600 mt-0.5" />
                  <p className="text-xs text-blue-700">
                    An email will be sent to the candidate with a link to register and fill in their details. The invitation expires in 7 days.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleQuickInvite} disabled={sendingInvite} data-testid="send-invite-btn">
                  {sendingInvite ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Invitation
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
